import { useCallback, useEffect, useState } from "react";
import { RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Logo } from "@/components/site/Logo";
import { http, apiError, brl } from "@/lib/api";

const STATUS = { approved: "Pago", pending: "Aguardando", in_process: "Em análise", rejected: "Recusado", cancelled: "Cancelado", refunded: "Estornado" };

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post("/admin/login", { password: pw });
      localStorage.setItem("maos_admin", data?.token ?? "");
      onLogin(data?.token);
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-32 max-w-sm space-y-5 rounded-2xl border border-white/10 bg-[#1A0A2E]/60 p-8">
      <h1 className="font-display text-4xl">Painel</h1>
      <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Senha" data-testid="admin-password-input"
        className="border-white/20 bg-white/5 text-white" />
      <button data-testid="admin-login-btn" className="w-full rounded-full bg-[var(--gold)] py-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#080312]">Entrar</button>
    </form>
  );
};

const Orders = ({ token, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [onlyPaid, setOnlyPaid] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/orders", { headers: { Authorization: `Bearer ${token}` } });
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      toast.error(apiError(err));
    }
  }, [token, onLogout]);
  useEffect(() => { load(); }, [load]);

  const setService = async (id, service_status) => {
    try {
      await http.patch(`/admin/orders/${id}/service`, { service_status }, { headers: { Authorization: `Bearer ${token}` } });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, service_status } : o)));
      toast.success("Atendimento atualizado");
    } catch (err) { toast.error(apiError(err)); }
  };

  const list = onlyPaid ? orders.filter((o) => o.status === "approved") : orders;
  const total = orders.filter((o) => o.status === "approved").reduce((s, o) => s + (o.amount ?? 0), 0);

  return (
    <div className="mx-auto max-w-7xl px-5 pb-20 pt-10 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold)]">Pedidos</p>
          <p data-testid="admin-total" className="font-display mt-2 text-5xl gold-text">{brl(total)}</p>
          <p className="text-sm text-white/60">recebido em leituras pagas</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-filter-btn" onClick={() => setOnlyPaid(!onlyPaid)} className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/80 hover:border-[var(--gold)]">
            {onlyPaid ? "Mostrar todos" : "Só pagos"}
          </button>
          <button data-testid="admin-refresh-btn" onClick={load} className="rounded-full border border-white/20 p-2 text-white/80 hover:border-[var(--gold)]"><RefreshCw className="h-4 w-4" /></button>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="rounded-full border border-white/20 p-2 text-white/80 hover:border-[var(--gold)]"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10">
        <Table data-testid="admin-orders-table">
          <TableHeader>
            <TableRow className="border-white/10">
              {["Data", "Cliente", "WhatsApp", "Leitura", "Perguntas", "Valor", "Forma", "Status", "Atendimento"].map((h) => <TableHead key={h} className="text-white/60">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-white/60">Nenhum pedido ainda.</TableCell></TableRow>}
            {list.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-${o.id}`} className="border-white/10">
                <TableCell className="whitespace-nowrap text-white/70">{new Date(o.created_at).toLocaleString("pt-BR")}</TableCell>
                <TableCell>{o.name}</TableCell>
                <TableCell><a className="text-[var(--gold)] hover:underline" href={`https://wa.me/55${o.whatsapp}`} target="_blank" rel="noreferrer">{o.whatsapp}</a></TableCell>
                <TableCell>{o.plan_label}</TableCell>
                <TableCell className="max-w-xs whitespace-pre-line text-white/75">{o.questions}</TableCell>
                <TableCell>{brl(o.amount)}</TableCell>
                <TableCell className="text-white/70">{o.provider === "stripe" ? "Cartão" : "Pix"}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-3 py-1 text-xs ${o.status === "approved" ? "bg-[var(--gold)] text-[#080312]" : "border border-white/20 text-white/70"}`}>{STATUS[o.status] ?? o.status}</span>
                </TableCell>
                <TableCell>
                  {o.status === "approved" ? (
                    <select data-testid={`admin-service-select-${o.id}`} value={o.service_status ?? "fila"} onChange={(e) => setService(o.id, e.target.value)}
                      className="rounded-lg border border-white/20 bg-[#1A0A2E] px-2 py-1.5 text-xs text-white">
                      <option value="fila">Na fila</option>
                      <option value="atendimento">Em atendimento</option>
                      <option value="concluido">Concluído</option>
                    </select>
                  ) : <span className="text-white/40">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("maos_admin") || "");
  const logout = useCallback(() => { localStorage.removeItem("maos_admin"); setToken(""); }, []);
  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 px-5 py-5 md:px-10"><Logo /></header>
      {token ? <Orders token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </div>
  );
}
